import {
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  Collapse,
  Box,
  IconButton,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { observer } from "mobx-react";
import debounce from "lodash/debounce";
import useStore from "../helpers/useStore";
import { PatientsContext } from "../stores/Patients/patients.provider";
import InfoIcon from "@mui/icons-material/Info";
import { useAppointmentsByRx } from "../stores/Appointment/appointment.queries";

export enum formType {
  SHORT = "Short",
  LONG = "Long",
}

interface ValidationCardProps {
  patientData: any;
  selectedRx: any;
  form: formType;
}

interface ValidationResult {
  label: string;
  validity: string;
  message: string;
}

const ValidationCard: FC<ValidationCardProps> = observer(
  ({ patientData, selectedRx, form }) => {
    const patientStore = useStore(PatientsContext);
    const { getLiveValidation } = patientStore;

    const [liveValidationData, setLiveValidationData] = useState<any>({});
    const [showPassedTests, setShowPassedTests] = useState(false);

    const rxId = selectedRx?.id ?? patientStore.selectedRxId ?? "";

    // Subscribing to this Rx's appointments re-runs validation when they change.
    const { data: rxAppointments } = useAppointmentsByRx(rxId);

    const debounceValidation = useCallback(
      debounce(async (currentFormData) => {
        const data = await getLiveValidation(currentFormData);
        setLiveValidationData(data);
      }, 100),
      [],
    );

    useEffect(() => {
      if (selectedRx && patientData) {
        // Attach patientId + Rx id so Blanko validators can find the patient's
        // other Blankos and exclude THIS Rx (otherwise it overlaps itself).
        debounceValidation({
          ...selectedRx,
          id: rxId || undefined,
          patientId: selectedRx?.patientId ?? patientData?.id,
        });
      }
    }, [selectedRx, patientData, debounceValidation, rxAppointments]);

    const getPersonalValidationStatus = (result: ValidationResult) => {
      if (result.validity === "Valid") {
        return {
          icon: <CheckCircleIcon sx={{ color: "success.main" }} />,
          message: result.message,
        };
      } else if (result.validity === "Not Valid") {
        return {
          icon: <ErrorIcon sx={{ color: "error.main" }} />,
          message: result.message,
        };
      } else if (result.validity === "Partially Valid") {
        return {
          icon: <WarningIcon sx={{ color: "warning.main" }} />,
          message: result.message,
        };
      } else if (result.validity === "Info") {
        return {
          icon: <InfoIcon sx={{ color: "info.main" }} />,
          message: result.message,
        };
      } else {
        return {
          icon: <ErrorIcon sx={{ color: "error.main" }} />,
          message: result.message,
        };
      }
    };

    const extractValidationResults = (validationObject: any) => {
      return Object.keys(validationObject).flatMap((key) => {
        const validation = validationObject[key];
        const results = [];
        if (validation) {
          // Push the main validation result
          results.push({
            label: validation.label,
            result: validation,
          });

          // Check for additional validation result
          if (validation.additionalInfo) {
            results.push({
              label: validation.additionalInfo.label,
              result: {
                validity: validation.additionalInfo.validity,
                message: validation.additionalInfo.message,
              },
            });
          }
        }
        return results;
      });
    };

    const patientValidationResults = patientData?.validationResults
      ? extractValidationResults(patientData.validationResults)
      : [];
    const liveValidationResults = liveValidationData
      ? extractValidationResults(liveValidationData)
      : [];
    const validationResultsAll = [
      ...patientValidationResults,
      ...liveValidationResults,
    ];
    // const rxValidationResults = selectedRx?.rxValidation ? extractValidationResults(selectedRx.rxValidation) : [];
    // const validationResultsAll = [...patientValidationResults, ...rxValidationResults];

    const validationResults = validationResultsAll.filter(
      ({ result }) => result,
    );

    validationResults.sort((a, b) => {
      if (a.result.validity === "Not Valid") return -1;
      if (b.result.validity === "Not Valid") return 1;
      if (a.result.validity === "Partially Valid") return -1;
      if (b.result.validity === "Partially Valid") return 1;
      if (a.result.validity === "Info") return -1;
      if (b.result.validity === "Info") return 1;
      return 0;
    });

    // Separate validations into issues and passed
    const issueValidations = validationResults.filter(
      ({ result }) =>
        result.validity === "Not Valid" ||
        result.validity === "Partially Valid" ||
        result.validity === "Info",
    );

    const passedValidations = validationResults.filter(
      ({ result }) => result.validity === "Valid",
    );

    const infoValidations = validationResults.filter(
      ({ result }) => result.validity === "Info",
    );

    // Always visible: Not Valid, Partially Valid, and Info
    const alwaysVisibleValidations = validationResults.filter(
      ({ result }) =>
        result.validity === "Not Valid" ||
        result.validity === "Partially Valid" ||
        result.validity === "Info",
    );

    const totalTests = validationResults.length;
    // Passed tests include both Valid and Info
    const passedTests = passedValidations.length + infoValidations.length;

    return (
      <Box>
        {/* Summary Header #fff9e5*/}
        <Box
          sx={{
            mb: 1,
            p: 1,
            backgroundColor: passedTests === totalTests ? "#def9d3" : "#ffe4e4",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "light",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
            <span style={{ fontWeight: "bold" }}>
              {passedTests}/{totalTests}
            </span>{" "}
            bestanden
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {validationResults.filter((v) => v.result.validity === "Not Valid")
              .length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontWeight: "bold",
                }}
              >
                <ErrorIcon sx={{ fontSize: 16, color: "error.main" }} />
                {
                  validationResults.filter(
                    (v) => v.result.validity === "Not Valid",
                  ).length
                }
              </Typography>
            )}
            {validationResults.filter(
              (v) => v.result.validity === "Partially Valid",
            ).length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontWeight: "bold",
                }}
              >
                <WarningIcon sx={{ fontSize: 16, color: "warning.main" }} />
                {
                  validationResults.filter(
                    (v) => v.result.validity === "Partially Valid",
                  ).length
                }
              </Typography>
            )}
          </Box>
        </Box>

        {/* Issues List (Always Visible) */}
        {alwaysVisibleValidations.length > 0 && (
          <List sx={{ py: 0 }}>
            {alwaysVisibleValidations.map(({ label, result }, index) => (
              <Tooltip
                key={`issue-${index}`}
                title={result.message}
                placement="right"
                arrow
              >
                <ListItem dense sx={{ padding: "2px 0px" }}>
                  <ListItemIcon sx={{ minWidth: "24px", marginRight: "8px" }}>
                    {getPersonalValidationStatus(result).icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    secondary={
                      form === formType.LONG
                        ? getPersonalValidationStatus(result).message
                        : null
                    }
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              </Tooltip>
            ))}
          </List>
        )}

        {/* Passed Tests (Collapsible) */}
        {passedValidations.length > 0 && (
          <Box>
            <Box
              onClick={() => setShowPassedTests(!showPassedTests)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                py: 0.5,
                px: 1,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                borderRadius: 1,
              }}
            >
              <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
                {showPassedTests ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
              <Typography
                variant="body2"
                sx={{ color: "success.main", fontWeight: "medium" }}
              >
                bestanden
              </Typography>
            </Box>
            <Collapse in={showPassedTests}>
              <List sx={{ py: 0, pl: 2 }}>
                {passedValidations.map(({ label, result }, index) => (
                  <Tooltip
                    key={`passed-${index}`}
                    title={result.message}
                    placement="right"
                    arrow
                  >
                    <ListItem dense sx={{ padding: "2px 0px" }}>
                      <ListItemIcon
                        sx={{ minWidth: "24px", marginRight: "8px" }}
                      >
                        {getPersonalValidationStatus(result).icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        secondary={
                          form === formType.LONG
                            ? getPersonalValidationStatus(result).message
                            : null
                        }
                        primaryTypographyProps={{ variant: "body2" }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </ListItem>
                  </Tooltip>
                ))}
              </List>
            </Collapse>
          </Box>
        )}
      </Box>
    );
  },
);

export default ValidationCard;
